// Material Issue — list + create + detail. Phase 1D-δ-1.
// Single-file for compactness. Route: /inventory/material-issues + ?create + ?id
//
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat halaman,
// saringan bawaan "Belum selesai" (draf + diajukan) + gudang sendiri (Admin Gudang); dialog MENAMBAH
// `?create`/`?id` ke alamat tanpa membuang posisi daftar. Pilihan bahan lewat `PemilihCari`.
import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ClipboardList, Plus, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { ISSUE_PURPOSES } from "../../lib/stockReasons";
import { formatDateTime } from "../../lib/format";
import { MaterialIssueStatusBadge } from "../../components/etams/StatusBadge";
import {
  listMaterialIssues, getMaterialIssue, createMaterialIssue,
  submitMaterialIssue, approveMaterialIssue, cancelMaterialIssue,
  listOpenRejectRemakes, apiErrorMessage,
} from "../../lib/inventoryApi";
import { ambilSemua } from "../../lib/ambilSemua";
import PemilihLotKain, { selisihLot } from "../../components/etams/PemilihLotKain";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";

const LABEL_SARING = { BELUM_SELESAI: "Belum selesai", DRAFT: "Draf", SUBMITTED: "Diajukan", APPROVED: "Disetujui", CANCELLED: "Dibatalkan" };
//: Cermin `URUT_SAH_MI` di backend/services/material_issue_service.py.
const URUT_SAH = ["created_at", "issue_number", "status"];
const KOLOM_CSV = [
  { kunci: "issue_number", judul: "Nomor" },
  { kunci: "wo", judul: "WO", ambil: (mi) => mi.wo?.wo_number || "" },
  { kunci: "store", judul: "Gudang", ambil: (mi) => mi.store?.name || "" },
  { kunci: "status", judul: "Status", ambil: (mi) => LABEL_SARING[mi.status] || mi.status },
  { kunci: "created_at", judul: "Tanggal", ambil: (mi) => formatDateTime(mi.created_at) },
  { kunci: "notes", judul: "Catatan" },
];

/** Tambah/hapus kunci dialog di alamat TANPA membuang keadaan daftar (h, q, saringan). */
function ubahParamDialog(setSp, ubah) {
  setSp((prev) => {
    const n = new URLSearchParams(prev);
    ["create", "id"].forEach((k) => n.delete(k));
    Object.entries(ubah || {}).forEach(([k, v]) => n.set(k, v));
    return n;
  }, { replace: true });
}
import { CAUSE_LABELS } from "../../lib/rejectRemakeApi";

// KONTRAS-01 Batch 9 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `INV_MATERIAL_ISSUE_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sepotong pun;
// `CANCELLED` bergeser dari MERAH ke netral — alasannya di berkas itu.


export default function MaterialIssues() {
  const { roleCode, profile } = useAuth();
  const [sp, setSp] = useSearchParams();
  const selectedId = sp.get("id");
  const showCreate = sp.get("create") === "1";

  const canWrite = ["OWNER", "KEPALA_CABANG", "ADMIN_PRODUKSI", "ADMIN_GUDANG"].includes(roleCode);
  // ⛔ KEPALA_CABANG DITAMBAHKAN 9 September 2026 (Celah 3, langkah 35).
  //    Wajib SELARAS dengan MI_APPROVE_ROLES di routers/material_issue.py —
  //    daftar ini SALINAN, dan salinan yang berselisih membuat tombol hilang
  //    untuk orang yang sebenarnya berhak (atau tampil untuk yang ditolak 403).
  //    RLS + role guard backend tetap otoritatif; ini cuma tampilan.
  const canApprove = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);

  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat.")));
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "BELUM_SELESAI", tampil: (v) => LABEL_SARING[v] || v },
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => stores.find(s => s.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listMaterialIssues(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listMaterialIssues(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat Material Issue."));
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);
  const tutupDialog = useCallback((refresh) => { ubahParamDialog(setSp, {}); if (refresh) load(); }, [setSp, load]);

  return (
    <div className="space-y-6" data-testid="mi-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" /> Material Issue ke WO
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Permintaan bahan baku untuk WO: Draf → Diajukan → Disetujui (stok keluar saat disetujui).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="mi-ke-barang-berkurang">
            <Link to="/inventory/barang-berkurang">Persetujuan barang berkurang</Link>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="mi-ke-telusur-lot">
            <Link to="/inventory/telusur-lot">Telusur lot kain</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => ubahParamDialog(setSp, { create: "1" })} data-testid="mi-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Buat MI
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari nomor / nomor WO / catatan" testId="mi-palang"
                    ekspor={{ nama: "pengeluaran_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="mi-status-filter" aria-label="Saring status">
          <option value="">Semua status</option>
          {Object.entries(LABEL_SARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="mi-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <ThUrut daftar={daftar} kunci="issue_number" className="px-4 py-3 font-medium">Nomor MI</ThUrut>
                <th className="px-4 py-3 font-medium">WO</th>
                <th className="px-4 py-3 font-medium">Gudang</th>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                <th className="px-4 py-3 font-medium">Catatan</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="7" className="p-12 text-center text-muted-foreground text-sm" data-testid="mi-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada Material Issue yang cocok dengan pencarian atau saringan ini."
                    : `Belum ada Material Issue.${canWrite ? " Klik ‘Buat MI’ untuk mulai." : ""}`}
                </td></tr>
              )}
              {daftar.tampil.map((mi) => (
                <tr key={mi.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 font-mono text-xs">{mi.issue_number}</td>
                  <td className="px-4 py-3 font-mono text-xs">{mi.wo?.wo_number || "—"}</td>
                  <td className="px-4 py-3">{mi.store?.name || "—"}</td>
                  <td className="px-4 py-3"><MaterialIssueStatusBadge value={mi.status} /></td>
                  <td className="px-4 py-3">{formatDateTime(mi.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{mi.notes || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm"
                            onClick={() => ubahParamDialog(setSp, { id: mi.id })}
                            data-testid={`mi-detail-${mi.id}`}>Detail</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="mi-paginasi" />
      </Card>

      {showCreate && (
        <MICreateDialog
          onClose={tutupDialog}
        />
      )}
      {selectedId && (
        <MIDetailDialog
          id={selectedId}
          canApprove={canApprove}
          canCancel={canWrite}
          profileId={profile?.id}
          onClose={tutupDialog}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Create MI dialog
// -----------------------------------------------------------------------
function MICreateDialog({ onClose }) {
  const [wos, setWos] = useState([]);
  const [woId, setWoId] = useState("");
  // Batch 2.3: tujuan pengeluaran. SENGAJA kosong di awal — API menolak
  // pengajuan tanpa tujuan, supaya bahan pengganti reject tidak pernah
  // diam-diam terhitung pemakaian normal.
  const [purpose, setPurpose] = useState("");
  const [rejectRemakeId, setRejectRemakeId] = useState("");
  const [openRejects, setOpenRejects] = useState([]);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([{ material_id: "", qty_requested: 1 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const woR = await ambilSemua((p) => api.get("/wo", { params: p }), { status: "IN_PROGRESS" }, { ukuran: 100 });
        setWos(woR || []);   // KELAS H: dulu 100 WO pertama — WO ke-101 nol bisa diberi bahan
        // Bahan TIDAK lagi dipindai utuh (28 Sep 2026) — tiap baris memakai PemilihCari (cari di server).
      } catch (e) {
        toast.error(apiErrorMessage(e, "Gagal memuat data WO/bahan."));
      }
    })();
  }, []);

  const setRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { material_id: "", qty_requested: 1 }]);
  const rmRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  // Tawarkan reject terbuka milik WO — supaya pengguna tidak perlu
  // mengingat atau menyalin id kejadian reject.
  useEffect(() => {
    setRejectRemakeId("");
    if (!woId || purpose !== "REJECT_REMAKE") { setOpenRejects([]); return; }
    listOpenRejectRemakes(woId)
      .then(r => setOpenRejects(r.data || []))
      .catch((e) => { setOpenRejects([]); toast.error(apiErrorMessage(e, "Daftar reject/remake terbuka gagal dimuat — muat ulang halaman.")); });
  }, [woId, purpose]);

  const submit = async () => {
    if (!woId) { toast.error("Pilih WO dulu."); return; }
    if (!purpose) {
      toast.error("Tujuan pengeluaran wajib dipilih — sistem tidak menebak.");
      return;
    }
    if (purpose === "REJECT_REMAKE" && !rejectRemakeId) {
      toast.error("Pilih kejadian reject/remake yang menyebabkan pengeluaran ini.");
      return;
    }
    for (const r of rows) {
      if (!r.material_id || !(r.qty_requested > 0)) {
        toast.error("Setiap item harus punya material & qty > 0.");
        return;
      }
      const pesanLot = selisihLot(r.lots, r.qty_requested);
      if (pesanLot) { toast.error(pesanLot); return; }
    }
    // S6: lot dikirim hanya bila diisi; baris lot kosong dibuang (lapis 1 opsional).
    const items = rows.map(({ lots, bahan, ...r }) => {
      const isi = (lots || []).filter(l => l.fabric_lot_id || l.qty);
      return isi.length ? { ...r, lots: isi.map(l => ({ fabric_lot_id: l.fabric_lot_id, qty: Number(l.qty) })) } : r;
    });
    setSaving(true);
    try {
      await createMaterialIssue({
        wo_id: woId, notes, items, purpose,
        reject_remake_id: purpose === "REJECT_REMAKE" ? rejectRemakeId : null,
      });
      toast.success("Material Issue dibuat sebagai DRAFT.");
      onClose(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membuat Material Issue."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="mi-create-dialog">
        <DialogHeader><DialogTitle>Material Issue Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm">WO (Sedang Produksi) <span className="text-danger-soft-foreground">*</span></span>
            <select value={woId} onChange={(e) => { setWoId(e.target.value); setRows(rs => rs.map(r => ({ ...r, lots: [] }))); }}
                    className="w-full h-9 rounded border px-2 text-sm" data-testid="mi-wo-select">
              <option value="">— Pilih WO —</option>
              {wos.map(w => <option key={w.id} value={w.id}>{w.wo_number}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">Tujuan pengeluaran <span className="text-danger-soft-foreground">*</span></span>
            <select value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    className="w-full h-9 rounded border px-2 text-sm"
                    data-testid="mi-purpose-select">
              <option value="">— Pilih tujuan —</option>
              {ISSUE_PURPOSES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              {ISSUE_PURPOSES.find(o => o.value === purpose)?.hint
                || "Menentukan apakah bahan ini terhitung biaya produksi normal atau biaya reject."}
            </span>
          </label>
          {purpose === "REJECT_REMAKE" && (
            <label className="block">
              <span className="text-sm">Kejadian reject/remake <span className="text-danger-soft-foreground">*</span></span>
              <select value={rejectRemakeId}
                      onChange={(e) => setRejectRemakeId(e.target.value)}
                      className="w-full h-9 rounded border px-2 text-sm"
                      data-testid="mi-reject-select">
                <option value="">— Pilih kejadian —</option>
                {openRejects.map(rr => (
                  <option key={rr.id} value={rr.id}>
                    {rr.type === "REMAKE" ? "Remake" : "Reject"} · {rr.qty_affected} pcs {rr.qty_target_type === "CELANA" ? "celana" : "atasan"} · {CAUSE_LABELS[rr.cause_code] || rr.cause_code}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                {openRejects.length === 0
                  ? "WO ini belum punya reject/remake terbuka. Catat rejectnya dulu di halaman WO."
                  : "Satu pengajuan = satu kejadian reject, supaya bahannya bisa ditelusuri balik."}
              </span>
            </label>
          )}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Item</span>
              <Button variant="outline" size="sm" onClick={addRow}>
                <Plus className="h-3 w-3 mr-1" /> Tambah Item
              </Button>
            </div>
            {rows.map((r, i) => (
              <div key={i} className="space-y-1">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <span className="text-xs text-muted-foreground">Bahan</span>
                  <PemilihCari value={r.bahan || null} className="w-full" placeholder="— Pilih —"
                               onChange={(m) => setRow(i, { bahan: m, material_id: m?.id || "", lots: [] })}
                               cari={cariBahan} label={labelBahan} testId={`mi-bahan-${i}`} />
                </div>
                <label className="w-32">
                  <span className="text-xs text-muted-foreground">Qty</span>
                  <Input type="number" step="0.001" value={r.qty_requested}
                         onChange={(e) => setRow(i, { qty_requested: Number(e.target.value) })} />
                </label>
                {rows.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => rmRow(i)}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <PemilihLotKain materialId={r.material_id} qty={r.qty_requested}
                              storeId={wos.find(w => w.id === woId)?.store_id}
                              value={r.lots} onChange={(lots) => setRow(i, { lots })} />
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-sm">Catatan (opsional)</span>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="mi-create-submit">
            {saving ? "Menyimpan..." : "Buat Draf"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Detail dialog
// -----------------------------------------------------------------------
function MIDetailDialog({ id, canApprove, canCancel, profileId, onClose }) {
  const [mi, setMi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getMaterialIssue(id);
      setMi(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat detail MI."));
      onClose(false);
    }
  }, [id, onClose]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (fn, msg) => {
    setBusy(true);
    try {
      await fn(id);
      toast.success(msg);
      await load();
      onClose(true);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    if (!cancelReason.trim() || cancelReason.trim().length < 5) {
      toast.error("Alasan batal wajib min 5 karakter.");
      return;
    }
    setBusy(true);
    try {
      await cancelMaterialIssue(id, { reason: cancelReason.trim() });
      toast.success("Material Issue dibatalkan.");
      onClose(true);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!mi) {
    return (
      <Dialog open onOpenChange={() => onClose(false)}>
        <DialogContent><div className="p-8 text-center text-muted-foreground">Memuat...</div></DialogContent>
      </Dialog>
    );
  }

  const isSelf = mi.requested_by === profileId;
  const canApproveThis = canApprove && mi.status === "SUBMITTED" && !isSelf;
  const canCancelThis = canCancel && ["DRAFT", "SUBMITTED"].includes(mi.status);
  const canSubmit = mi.status === "DRAFT";

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl" data-testid="mi-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mi.issue_number} <MaterialIssueStatusBadge value={mi.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Dibuat:</span> {formatDateTime(mi.created_at)}</div>
            <div><span className="text-muted-foreground">Diajukan:</span> {formatDateTime(mi.submitted_at)}</div>
            <div><span className="text-muted-foreground">Disetujui:</span> {formatDateTime(mi.approved_at)}</div>
            <div><span className="text-muted-foreground">Dibatalkan:</span> {formatDateTime(mi.cancelled_at)}</div>
          </div>
          {mi.notes && <div className="text-sm"><span className="text-muted-foreground">Catatan:</span> {mi.notes}</div>}
          {mi.cancel_reason && (
            <div className="text-sm bg-muted/50 border border-border rounded p-2">
              <span className="font-medium">Alasan Batal:</span> {mi.cancel_reason}
            </div>
          )}
          <div>
            <div className="text-sm font-medium mb-2">Item ({mi.items?.length || 0})</div>
            <table className="w-full text-sm border">
              <thead className="bg-muted/50 border-b">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Bahan</th>
                  <th className="px-3 py-2 font-medium">Qty Diminta</th>
                  <th className="px-3 py-2 font-medium">Qty Dikeluarkan</th>
                  <th className="px-3 py-2 font-medium">Pengecualian</th>
                  <th className="px-3 py-2 font-medium">Lot kain</th>
                </tr>
              </thead>
              <tbody>
                {(mi.items || []).map(it => (
                  <tr key={it.id} className="border-b">
                    <td className="px-3 py-2 text-xs" data-testid={`mi-detail-bahan-${it.id}`}>
                      {it.material_name || it.material?.name || "—"}
                      {it.material_code && <span className="block text-muted-foreground font-mono">{it.material_code}</span>}
                    </td>
                    <td className="px-3 py-2">{it.qty_requested}</td>
                    <td className="px-3 py-2">{it.qty_issued ?? "-"}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">
                      {it.override_reason ? `Ya: ${it.override_reason}` : "-"}
                    </td>
                    <td className="px-3 py-2 text-xs" data-testid={`mi-detail-lot-${it.id}`}>
                      {(it.lots || []).length
                        ? it.lots.map(l => `${l.lot_code} (${l.qty})`).join(", ")
                        : <span className="text-muted-foreground">tidak dicatat</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onClose(false)}>Tutup</Button>
          {canSubmit && (
            <Button onClick={() => doAction(submitMaterialIssue, "MI diajukan.")}
                    disabled={busy} data-testid="mi-submit-btn">
              Ajukan
            </Button>
          )}
          {canApproveThis && (
            <Button onClick={() => doAction(approveMaterialIssue, "MI disetujui & stok keluar dicatat.")}
                    disabled={busy} data-testid="mi-approve-btn">
              Setujui
            </Button>
          )}
          {canCancelThis && !showCancel && (
            <Button variant="destructive" onClick={() => setShowCancel(true)}
                    data-testid="mi-cancel-open">
              Batalkan
            </Button>
          )}
          {showCancel && (
            <div className="w-full space-y-2 pt-2 border-t">
              <Textarea placeholder="Alasan pembatalan (min 5 karakter)..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={2} data-testid="mi-cancel-reason" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCancel(false)}>Kembali</Button>
                <Button variant="destructive" size="sm" onClick={doCancel} disabled={busy}
                        data-testid="mi-cancel-confirm">Konfirmasi Batal</Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
